/** Pure filter helpers for LiveDashboardPage (easy to test). */

/** Matches ``SUSPENSION_TRIGGERS`` in services/opta_live_connect/transformations.py */
export const SUSPENSION_TRIGGER_TYPE_IDS = new Set([
  6, 16, 10, 17, 38, 39, 60, 63, 78, 85, 86, 87, 88,
]);

/**
 * Feeds whose close triggers are recorded in ``block_tags`` rather than as an event type id.
 *
 * Opta is the only feed that answers "did this fire a close rule?" from ``type_id``. Every
 * push feed (SportRadar, Genius) names the triggers it armed in ``block_tags``, so this is a
 * property of the feed's *shape*, not a list of vendors to keep extending — a new push feed
 * lands here automatically by not being Opta.
 */
export function usesBlockTags(ev) {
  const src = String(ev?.feed_source || '').trim().toLowerCase();
  return Boolean(src) && src !== 'opta';
}

export function isSportRadarEvent(ev) {
  return String(ev?.feed_source || '').trim().toLowerCase() === 'sportradar';
}

export function isGeniusEvent(ev) {
  return String(ev?.feed_source || '').trim().toLowerCase() === 'genius';
}

/** ``block_tags`` is jsonb: an array over the API, but tolerate a raw string. */
export function eventBlockTags(ev) {
  const raw = ev?.block_tags;
  if (Array.isArray(raw)) return raw.map((t) => String(t || '').trim()).filter(Boolean);
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.map((t) => String(t || '').trim()).filter(Boolean)
        : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Did this row fire a close rule?
 *
 * The feeds answer this in completely different places, and conflating them was wrong in
 * both directions. ``SUSPENSION_TRIGGER_TYPE_IDS`` holds *Opta* type ids; SportRadar rows
 * carry the football module's own ``EVENT_TYPE_IDS``, an unrelated id space that happens to
 * overlap — ``extra_point`` is 6 and ``faircatch_kick`` is 10, both of which are Opta
 * suspension ids. So every extra point was painted red and matched the "Blocking" filter,
 * while the football triggers that actually closed the market (scoring_play, turnover,
 * third_down_fail …) matched nothing, because they live in ``block_tags``.
 *
 * Genius rows would have hit the same trap from the other side: every one carries the same
 * ``type_id``, so an Opta-id test marks them all as never blocking, while the risks that
 * actually closed the market sit in ``block_tags``. Keyed on feed shape, not vendor.
 */
export function isSuspensionTrigger(ev) {
  if (usesBlockTags(ev)) return eventBlockTags(ev).length > 0;
  const typeId = ev?.type_id != null ? Number(ev.type_id) : NaN;
  return Number.isFinite(typeId) && SUSPENSION_TRIGGER_TYPE_IDS.has(typeId);
}

/**
 * Opta's ``x > 80`` danger zone. SportRadar rows have no ``x`` at all (it is always null), so
 * the old comparison was quietly always false; the football equivalent is the offence-side
 * close, which is fixture state rather than a property of any one row.
 */
export function isDangerZone(ev) {
  if (usesBlockTags(ev)) return false;
  const x = ev?.x != null ? Number(ev.x) : NaN;
  return Number.isFinite(x) && x > 80;
}

/**
 * Is the "player rows only" filter meaningful for this set?
 *
 * On football rows ``player_name`` holds the offence's team alias, not a player, so the
 * filter would silently mean "plays, not stoppages" — a different question wearing the
 * wrong label.
 */
export function supportsPlayerFilter(events) {
  return !(events || []).some(usesBlockTags);
}

/** Rows styled red (suspension) or yellow (x &gt; 80) on the live dashboard. */
export function isHighlightedEventRow(ev) {
  return isSuspensionTrigger(ev) || isDangerZone(ev);
}

/** Optic ``game_id`` or Opta ``match_opta_id`` — one fixture identity. */
export function eventFixtureId(ev) {
  const fid = ev?.optic_fixture_id ?? ev?.match_opta_id;
  return fid != null ? String(fid).trim() : '';
}

/** Natural key: one Opta matchevent row per fixture (``opta_row_id`` = Opta event ``id``). */
export function eventBusinessKey(ev) {
  const fid = eventFixtureId(ev);
  const rowId = ev?.opta_row_id;
  if (!fid || rowId == null) return '';
  return `${fid}:${rowId}`;
}

export function eventTypeLabel(ev) {
  if (ev?.type_name != null && String(ev.type_name).trim()) {
    return String(ev.type_name).trim();
  }
  if (ev?.type_id != null && ev.type_id !== '') return `Type ${ev.type_id}`;
  return '';
}

export function hasPlayerName(ev) {
  return ev?.player_name != null && String(ev.player_name).trim() !== '';
}

/**
 * @param {object[]} events
 * @param {{
 *   playerEventsOnly: boolean,
 *   selectedTypes: string[],
 *   blockedRowsOnly?: boolean,
 * }} filters
 */
export function applyEventFilters(
  events,
  { playerEventsOnly, selectedTypes, blockedRowsOnly = false }
) {
  const types =
    selectedTypes && selectedTypes.length > 0 ? new Set(selectedTypes) : null;
  return events.filter((ev) => {
    if (playerEventsOnly && !hasPlayerName(ev)) return false;
    if (types && !types.has(eventTypeLabel(ev))) return false;
    if (blockedRowsOnly && !isHighlightedEventRow(ev)) return false;
    return true;
  });
}

export function distinctTypeLabels(events) {
  const names = new Set();
  for (const ev of events) {
    const label = eventTypeLabel(ev);
    if (label) names.add(label);
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}

/** Periods 1–2 sort by match half; higher Opta period ids bucket with 0 (avoid sticking above P2). */
export function eventSortPeriod(ev) {
  const period = Number(ev?.period_id) || 0;
  return period > 2 ? 0 : period;
}

/** Descending sort key: period → match clock → event id (latest at top within each period). */
export function eventChronologyKey(ev) {
  const period = eventSortPeriod(ev);
  const min = Number(ev?.time_min) || 0;
  const sec = Number(ev?.time_sec) || 0;
  const eid = Number(ev?.event_id) || Number(ev?.opta_row_id) || 0;
  return period * 1_000_000 + min * 10_000 + sec * 100 + eid;
}

/** ``qualifiers`` is jsonb: a dict over the API, but tolerate a raw string. */
export function eventQualifiers(ev) {
  const raw = ev?.qualifiers;
  if (raw && typeof raw === 'object') return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * SportRadar's own per-game monotonic ordering (epoch ms), carried on every push event.
 *
 * It is the only usable ordering for those rows: ``time_min`` / ``time_sec`` are null on
 * them (the clock is a "12:24" string, and it counts *down*, so it cannot be added to a
 * key that counts up), and ``event_id`` holds 31 bits of a hash of the SportRadar UUID.
 * That hash reaches ~2.1e9 while ``period * 1_000_000`` tops out at 4e6, so it dominated
 * :func:`eventChronologyKey` completely and shuffled the table into hash order.
 */
export function eventSequenceMs(ev) {
  const q = eventQualifiers(ev);
  const raw = q ? q.sequence : undefined;
  const ms = raw != null && raw !== '' ? Number(raw) : NaN;
  return Number.isFinite(ms) ? ms : null;
}

export function eventReceivedMs(ev) {
  const raw = ev?.received_at_utc;
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * ``{kind, value}`` — both descending, ``kind`` grouping rows that share a scale.
 *
 * A fixture only ever carries one feed's rows, so the kinds never actually interleave; the
 * grouping is what keeps the comparator transitive if they ever did.
 */
export function eventOrderKey(ev) {
  const seq = eventSequenceMs(ev);
  if (seq != null) return { kind: 0, value: seq };
  // A SportRadar row with no sequence: arrival order beats a key built from null clocks.
  if (String(ev?.feed_source || '').trim().toLowerCase() === 'sportradar') {
    return { kind: 1, value: eventReceivedMs(ev) ?? 0 };
  }
  return { kind: 2, value: eventChronologyKey(ev) };
}

export function compareEventsChronologyDesc(a, b) {
  const ka = eventOrderKey(a);
  const kb = eventOrderKey(b);
  if (ka.kind !== kb.kind) return ka.kind - kb.kind;
  if (ka.value !== kb.value) return kb.value - ka.value;
  // Stable, and keeps republished revisions of one action adjacent.
  return String(b?.opta_row_id ?? '').localeCompare(String(a?.opta_row_id ?? ''));
}

/** Gate-open (offered) markets first; then higher window fees; then market_key. */
export function isMarketOffered(m) {
  return Boolean(m?.gate_allowed) && !Boolean(m?.manual_suspend);
}

export function compareMarketsOfferedFirst(a, b) {
  const tierA = isMarketOffered(a) ? 0 : 1;
  const tierB = isMarketOffered(b) ? 0 : 1;
  if (tierA !== tierB) return tierA - tierB;
  const feeA = Number(a?.fees_window);
  const feeB = Number(b?.fees_window);
  const fa = Number.isFinite(feeA) ? feeA : 0;
  const fb = Number.isFinite(feeB) ? feeB : 0;
  if (fb !== fa) return fb - fa;
  return String(a?.market_key || '').localeCompare(String(b?.market_key || ''));
}

/** Dedupe by fixture + opta_row_id (matches DB unique index). */
export function dedupeEventsByFixtureRow(events) {
  const seen = new Set();
  const out = [];
  for (const ev of events) {
    const key = eventBusinessKey(ev);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(ev);
  }
  return out;
}

/* ---------------------------------------------------------------------------------------
 * American football event display.
 *
 * The base columns are Opta's (player / x / y / outcome) and say nothing about a football
 * play: `player_name` holds the offence's alias, `outcome` holds the *end* down, and x/y are
 * always null. Everything that makes a play legible — down, distance, field position, yards,
 * a description — is parsed and persisted in `qualifiers`; these read it back. Both American
 * football feeds write the same `qualifiers` vocabulary, so these need no vendor branch:
 * SportRadar fills `start_*` from the play's own start situation and Genius from `nextPlay`,
 * and the description is SportRadar's play text or Genius's active-risk summary.
 * ------------------------------------------------------------------------------------- */

export function isAmericanFootballEvent(ev) {
  return String(eventQualifiers(ev)?.sport || '') === 'american_football';
}

/** True when any row in the set is a football play, i.e. the table should switch columns. */
export function hasAmericanFootballEvents(events) {
  return (events || []).some(isAmericanFootballEvent);
}

const DOWN_ORDINALS = { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th' };

/**
 * Number, or null when the field is absent.
 *
 * ``Number(null)`` is ``0`` and ``Number('')`` is ``0``, so a plain ``Number.isFinite``
 * guard turns every missing football field into a real-looking zero — a timeout would show
 * "0" yards on the "RUTG 0" yardline at 0-0.
 */
function numberOrNull(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function formatDownDistance(ev) {
  const q = eventQualifiers(ev);
  if (!q) return '';
  const down = numberOrNull(q.start_down);
  if (down == null || down <= 0) return '';
  const label = DOWN_ORDINALS[down] || `${down}th`;
  const yfd = numberOrNull(q.start_yfd);
  // Goal-to-go arrives as yfd equal to the yardline; showing "& 3" is still truthful.
  return yfd == null ? label : `${label} & ${yfd}`;
}

/** ``RUTG 3`` — the bare yardline number is ambiguous without whose half it is on. */
export function formatFieldPosition(ev) {
  const q = eventQualifiers(ev);
  if (!q) return '';
  const yardline = numberOrNull(q.start_yardline);
  if (yardline == null) return '';
  const side = q.start_yardline_side ? String(q.start_yardline_side).trim() : '';
  return side ? `${side} ${yardline}` : String(yardline);
}

/** Signed yardage for the play; '' when the event carries no magnitude (timeouts etc.). */
export function formatPlayYards(ev) {
  const q = eventQualifiers(ev);
  const yards = q ? numberOrNull(q.yards) : null;
  if (yards == null) return '';
  return yards > 0 ? `+${yards}` : String(yards);
}

/** SportRadar's own play description — the single most informative field on the row. */
export function eventDescription(ev) {
  const q = eventQualifiers(ev);
  const text = q && q.description != null ? String(q.description).trim() : '';
  return text;
}

/** ``home-away`` points after the play, when the event carries a score. */
export function formatEventScore(ev) {
  const q = eventQualifiers(ev);
  if (!q) return '';
  const home = numberOrNull(q.home_points);
  const away = numberOrNull(q.away_points);
  if (home == null || away == null) return '';
  return `${home}-${away}`;
}

/** Offence alias for the play (``possession_alias``), falling back to the stored column. */
export function eventOffense(ev) {
  const q = eventQualifiers(ev);
  const alias = q && q.start_possession != null ? String(q.start_possession).trim() : '';
  if (alias) return alias;
  return ev?.player_name ? String(ev.player_name).trim() : '';
}

/** ``13:41 Q2`` — the clock counts down, so the period has to be shown alongside it. */
export function formatFootballClock(ev) {
  const clock = ev?.match_clock ? String(ev.match_clock).trim() : '';
  const period = ev?.period_id != null && ev.period_id !== '' ? `Q${ev.period_id}` : '';
  if (clock && period) return `${clock} ${period}`;
  return clock || period || '—';
}
